import { JobIdFromSourceParams, JobIdFromTextParams } from '@anm/api/modules/storyboard';
import NewVideoButton from '@anm/components/NewVideoButton';
import { trackCreateVideoFromAI } from '@anm/components/NewVideoButton/helpers';
import ButtonLoader from '@anm/components/buttons/ButtonLoader';
import { isModalOpen } from '@anm/components/modals/Modal';
import { useCallback, FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import config from '../../../config';
import routes from '../../../routes';
import { authSelectors } from '../../../store/modules/auth';
import { appMetaSelectors } from '../../../store/modules/appMeta';
import { projectSelectors } from '../../../store/modules/project';
import { storyboardActions, storyboardSelectors } from '../../../store/modules/storyboard';
import { userSelectors } from '../../../store/modules/user';
import { videoFolderActions, videoFolderSelectors } from '../../../store/modules/videoFolder';
import MyProjectsSearch from '../MyProjectsSearch';
import UploadVideoButton from '../UploadVideoButton';
import useProjectsPermissions from '../hooks/useProjectsPermissions';

import { CreateNewFolderButton } from './Wrapper';
import useUserTimeList from './hooks/userUserTimeList';
import { MyProjectsActionsProps } from './types';
import { uploadsSelectors } from '@anm/store/modules/uploads';

const { Link } = routes;
const { urls, isProd } = config();

const MyProjectsActions: FC<MyProjectsActionsProps> = ({ onCreateNewFolder }) => {
  const dispatch = useDispatch();
  const { canCreateProject } = useProjectsPermissions();

  const isSearchOpen = useSelector(projectSelectors.selectSearchOpenStatus);
  const videoFolder = useSelector(videoFolderSelectors.selectVideoFolders);
  const isUploadPending = useSelector(uploadsSelectors.selectIsUploadWithProjectCreationPending);
  const isCreateProjectPending = useSelector(videoFolderSelectors.selectCreatedProjectPending);
  const isUserFree = useSelector(userSelectors.selectIsUserFree);
  const jobId = useSelector(storyboardSelectors.selectJobId);
  const isPendingJobId = useSelector(storyboardSelectors.selectJobIdIsPending);
  const isUserLogged = useSelector(authSelectors.isUserLogged);
  const workspace = useSelector(appMetaSelectors.selectWorkspace);

  const isPending = !!(isUploadPending || isCreateProjectPending);

  const parentId = videoFolder.data?.id;

  const handleCreateNewFolder = useCallback(() => {
    onCreateNewFolder();
    parentId && dispatch(videoFolderActions.createFolder({ parentId }));
  }, [parentId, onCreateNewFolder]);

  const onClearJobId = () => dispatch(storyboardActions.clearJobId());
  const onCreateVideoFromLink = (params: JobIdFromSourceParams) => dispatch(storyboardActions.fetchUrlJobId(params));
  const onCreateVideoFromText = (params: JobIdFromTextParams) =>
    dispatch(storyboardActions.fetchTextJobId({ workspace, ...params }));

  const jobParams = useSelector(storyboardSelectors.selectJobParams);

  const timeList = useUserTimeList();

  if (!videoFolder.data || !parentId) return null;

  const renderLinkWrapper = (children: JSX.Element) => (
    <Link route="video-templates" params={{ folderId: parentId }}>
      {children}
    </Link>
  );

  return (
    <>
      <MyProjectsSearch isAlwaysOpen={!canCreateProject} />
      {!isSearchOpen && canCreateProject && (
        <>
          <NewVideoButton
            {...{
              jobId,
              isProd,
              timeList,
              workspace,
              isUserFree,
              isUserLogged,
              isPendingJobId,
              renderLinkWrapper,
              onCreateVideoFromLink,
              onCreateVideoFromText
            }}
            waveUrl={urls.wave}
            apiUrl={urls.apiWave}
            editorUrl={urls.editor}
            clearJobId={onClearJobId}
            onCreateVideo={params => trackCreateVideoFromAI({ params, jobParams })}
            currentFolderId={parentId}
            streamingStudioUrl={urls.streamingStudio}
          />
          <UploadVideoButton disabled={isPending}>
            <ButtonLoader
              size="large"
              variant="white-blue-border-share"
              icon="images/dashboard/upload-blue.svg"
              buttonType="container"
            >
              Upload Video
            </ButtonLoader>
          </UploadVideoButton>
          <CreateNewFolderButton
            color="blue"
            isPending={!isModalOpen && videoFolder.newFolder.isPending}
            size="large"
            data-button="create New Folder"
            variant="white-blue-border-share"
            icon="images/dashboard/new-folder-v1.svg"
            onClick={handleCreateNewFolder}
          >
            New Folder
          </CreateNewFolderButton>
        </>
      )}
    </>
  );
};

export default MyProjectsActions;
